import { useEffect, useMemo, useRef, useState } from 'react'
import type { StrategyMode, StageAllocation, StrategyAllocationResponse } from '../../types/strategy'
import type { AllocationSimulationResponse } from '../../types/simulation'
import type { LpState } from '../../types/lp'
import { simulateAllocation } from '../../api/simulation'
import { fetchStrategyAllocation, fetchStrategyState } from '../../api/strategy'
import { useWalletBalances } from '../../hooks/useWalletBalance'
import { useWalletAdapterBridge } from '../../lib/walletBridge'
import { Spinner } from '../common/Spinner'
import { WizardProgressBar } from './WizardProgressBar'
import { StrategyStep } from './StrategyStep'
import { AmountInput, checkAmount, maxAmountString } from './AmountInput'
import { SwapStep } from './SwapStep'
import { WizardStageStep } from './WizardStageStep'
import { SummaryStep } from './SummaryStep'


// Wait for typing to settle before asking the backend for a plan.
const AMOUNT_DEBOUNCE_MS = 500

function visibleStageKeys(result: AllocationSimulationResponse | null): string[] {
  const base = ['STAKE', 'LEND', 'BORROW', 'LP']
  if (!result) return base
  if (result.allocation.hedge.status !== 'SKIPPED') base.push('HEDGE')
  return base
}

interface Props {
  portfolioId: string | null
  wsLp?: LpState[] | null
}

export function WizardView({ portfolioId, wsLp }: Props) {
  // No default amount: the user chooses it, capped by what the backend says is in their wallet.
  const [amount, setAmount] = useState('')
  const [maxUsd, setMaxUsd] = useState<number | null>(null)
  const [maxLoaded, setMaxLoaded] = useState(false)
  const [mode, setMode] = useState<StrategyMode>('BALANCED_CARRY')
  const [result, setResult] = useState<AllocationSimulationResponse | null>(null)
  // "amount|mode" the current `result` was computed for: a plan is only usable for exactly that.
  const [resultKey, setResultKey] = useState<string | null>(null)
  // The user confirmed a strategy whose plan is still being computed: go on once it arrives.
  const [advanceWhenReady, setAdvanceWhenReady] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stepIndex, setStepIndex] = useState(0)
  const requestIdRef = useRef(0)
  const portfolioRef = useRef(portfolioId)
  useEffect(() => { portfolioRef.current = portfolioId }, [portfolioId])


  const [realAlloc, setRealAlloc] = useState<StrategyAllocationResponse | null>(null)
  const realFetchedModeRef = useRef<string | null>(null)
  // "amount|mode" of the latest simulation request, so the debounced effect and an explicit one
  // never ask twice for the same plan.
  const lastSimKeyRef = useRef<string | null>(null)

  const balances = useWalletBalances(portfolioId)
  useWalletAdapterBridge()

  const stageKeys = useMemo(() => visibleStageKeys(result), [result])
  const totalSteps = 2 + stageKeys.length + 1

  const amountCheck = checkAmount(amount, maxUsd)
  const validAmount = amountCheck.ok ? amountCheck.value : null
  const planReady = result != null && validAmount != null && resultKey === `${validAmount}|${mode}`

  // Learn what sits in the wallet right now: the ceiling for the amount (money already in DeFi
  // positions is not available to put to work). Called on load, before a Max fill, and when the
  // server says the amount was too much (the balance moved meanwhile). Uses the light strategy
  // endpoint — the same wallet figure the server's own cap uses — so it does not depend on the
  // chosen strategy either.
  const refreshMax = async (): Promise<number | null> => {
    const startedFor = portfolioId
    if (!startedFor) return null
    try {
      const state = await fetchStrategyState(startedFor)
      if (portfolioRef.current !== startedFor) return null
      const next = Math.max(0, Number(state.walletUsd))
      if (!Number.isFinite(next)) return null
      setMaxUsd(next)
      return next
    } catch {
      return null   // keep whatever limit we already have
    }
  }

  const simulate = (nextAmount: string, nextMode: StrategyMode) => {
    const parsed = parseFloat(nextAmount || '0')
    if (!Number.isFinite(parsed) || parsed <= 0) return
    lastSimKeyRef.current = `${nextAmount}|${nextMode}`
    const rid = ++requestIdRef.current
    setLoading(true)
    setError(null)
    simulateAllocation(nextAmount, nextMode)
      .then(r => {
        if (rid !== requestIdRef.current) return
        setResult(r)
        setResultKey(`${nextAmount}|${nextMode}`)
      })
      .catch(e => {
        if (rid !== requestIdRef.current) return
        setAdvanceWhenReady(false)
        lastSimKeyRef.current = null   // whatever failed, the same plan may be asked for again
        const message = e instanceof Error ? e.message : 'Strategy simulation failed'
        if (message.startsWith('422')) {
          // The amount was within the limit we showed but not within the server's: the balance
          // moved. Refresh the limit and let the same amount be tried again.
          setError('Your wallet balance changed — the limit was refreshed. Check the amount and try again.')
          void refreshMax()
        } else {
          setError(message)
        }
      })
      .finally(() => { if (rid === requestIdRef.current) setLoading(false) })
  }

  // New wallet: forget everything about the previous one, then learn what this one holds. The
  // backend's wallet balance is the ceiling for the amount.
  useEffect(() => {
    requestIdRef.current++
    lastSimKeyRef.current = null
    setAmount('')
    setResult(null)
    setResultKey(null)
    setAdvanceWhenReady(false)
    setStepIndex(0)
    setRealAlloc(null)
    realFetchedModeRef.current = null
    setMaxUsd(null)
    setMaxLoaded(false)
    setLoading(false)
    setError(null)
    if (!portfolioId) return
    let cancelled = false
    refreshMax().finally(() => { if (!cancelled) setMaxLoaded(true) })
    return () => { cancelled = true }
  }, [portfolioId])

  // Plan for the amount the user typed, once it is valid and the typing has settled. An invalid or
  // cleared amount drops the old plan: nothing is shown for money that was not asked about.
  useEffect(() => {
    if (!validAmount) {
      requestIdRef.current++
      lastSimKeyRef.current = null
      setResult(null)
      setResultKey(null)
      setAdvanceWhenReady(false)
      setLoading(false)
      return
    }
    if (lastSimKeyRef.current === `${validAmount}|${mode}`) return
    const timer = setTimeout(() => simulate(validAmount, mode), AMOUNT_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [validAmount, mode])

  const handleStrategyConfirm = (nextMode: StrategyMode) => {
    if (!validAmount) return
    if (nextMode !== mode) {
      // A different strategy needs its own plan: move on when it arrives, not before.
      setMode(nextMode)
      simulate(validAmount, nextMode)
      setAdvanceWhenReady(true)
      return
    }
    if (planReady) setStepIndex(1)
  }

  useEffect(() => {
    if (advanceWhenReady && planReady) {
      setAdvanceWhenReady(false)
      setStepIndex(1)
    }
  }, [advanceWhenReady, planReady])

  // A later step is only meaningful with a plan for the chosen amount; never leave the user on a
  // blank page without one.
  useEffect(() => {
    if (stepIndex > 0 && !planReady && !advanceWhenReady) setStepIndex(0)
  }, [stepIndex, planReady, advanceWhenReady])

  // Max uses the freshest balance we can get, falling back to the one already loaded.
  const handleMax = async () => {
    const startedFor = portfolioId
    const fresh = await refreshMax()
    if (portfolioRef.current !== startedFor) return
    const limit = fresh ?? maxUsd
    if (limit != null) setAmount(maxAmountString(limit))
  }

  const stageMap: Record<string, StageAllocation> = result ? {
    STAKE: result.allocation.stake,
    LEND: result.allocation.lend,
    BORROW: result.allocation.borrow,
    LP: result.allocation.lp,
    HEDGE: result.allocation.hedge,
  } : {}

  const isStageStep = stepIndex >= 2 && stepIndex < 2 + stageKeys.length
  const currentStageKey = isStageStep ? stageKeys[stepIndex - 2] : null
  const currentStage = currentStageKey ? stageMap[currentStageKey] : undefined
  const isSummary = stepIndex === totalSteps - 1

  useEffect(() => {
    if (!isSummary || !portfolioId || realFetchedModeRef.current === mode) return
    realFetchedModeRef.current = mode
    fetchStrategyAllocation(portfolioId, mode)
      .then(r => setRealAlloc(r))
      .catch(() => {})
  }, [isSummary, portfolioId, mode])

  const realStageMap: Record<string, StageAllocation> | null = realAlloc ? {
    STAKE:  realAlloc.stake,
    LEND:   realAlloc.lend,
    BORROW: realAlloc.borrow,
    LP:     realAlloc.lp,
    HEDGE:  realAlloc.hedge,
  } : null

  const handleGoToStage = (stageKey: string) => {
    const idx = stageKeys.indexOf(stageKey)
    if (idx >= 0) setStepIndex(2 + idx)
  }

  return (
    <main className="mx-auto max-w-2xl px-3 py-4 sm:px-5 sm:py-6">
      <WizardProgressBar
        stepIndex={stepIndex}
        stageKeys={stageKeys}
        onStepClick={setStepIndex}
        maxStep={planReady ? totalSteps - 1 : 0}
      />

      {error && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-300">
          <span>{error}</span>
          {validAmount && (
            <button
              type="button"
              onClick={() => simulate(validAmount, mode)}
              className="shrink-0 rounded-md border border-red-400/40 px-3 py-1 text-xs font-medium text-red-200 hover:bg-red-500/10"
            >
              Retry
            </button>
          )}
        </div>
      )}

      {stepIndex === 0 && (
        <StrategyStep
          mode={mode}
          result={result}
          loading={loading}
          canContinue={planReady}
          amountSlot={
            <AmountInput
              value={amount}
              onChange={setAmount}
              maxUsd={maxUsd}
              maxLoaded={maxLoaded}
              signedIn={portfolioId != null}
              message={amountCheck.ok ? null : amountCheck.message}
              onMax={handleMax}
            />
          }
          onConfirm={handleStrategyConfirm}
        />
      )}

      {stepIndex === 1 && (
        <SwapStep
          result={result}
          balances={balances}
          onNext={() => setStepIndex(2)}
          onBack={() => setStepIndex(0)}
        />
      )}

      {isStageStep && currentStageKey && currentStage && (
        <WizardStageStep
          stageKey={currentStageKey}
          stage={currentStage}
          basePrice={result?.allocation.marketContext.basePrice ?? 0}
          projectedHf={result?.allocation.projectedHealthFactor ?? 0}
          balances={balances}
          lpPositions={wsLp}
          onNext={() => setStepIndex(i => Math.min(i + 1, totalSteps - 1))}
          onBack={() => setStepIndex(i => i - 1)}
          isLast={stepIndex === totalSteps - 2}
        />
      )}

      {isSummary && result && (
        <SummaryStep
          result={result}
          stageKeys={stageKeys}
          stageMap={stageMap}
          realStageMap={realStageMap}
          onBack={() => setStepIndex(totalSteps - 2)}
          onRestart={() => setStepIndex(0)}
          onExecute={() => setStepIndex(2)}
          onGoToStage={handleGoToStage}
        />
      )}

      {stepIndex === 0 && loading && !result && (
        <div className="mt-8 flex items-center justify-center gap-2 text-gray-500">
          <Spinner size="sm" />
          <span className="text-sm">Loading live rates…</span>
        </div>
      )}
    </main>
  )
}
